import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

const SearchableDropdown = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select...',
    valueKey = 'value',
    labelKey = 'label',
    clearable = true,
    disabled = false,
    loading = false,
    small = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const containerRef = useRef(null);
    const buttonRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
        });
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                containerRef.current && !containerRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Position and focus when opening
    useEffect(() => {
        if (isOpen) {
            updatePosition();
            if (inputRef.current) inputRef.current.focus({ preventScroll: true });
        }
    }, [isOpen, updatePosition]);

    // Reposition on scroll/resize
    useEffect(() => {
        if (!isOpen) return;
        const handleReposition = () => updatePosition();
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
        return () => {
            window.removeEventListener('scroll', handleReposition, true);
            window.removeEventListener('resize', handleReposition);
        };
    }, [isOpen, updatePosition]);

    const filteredOptions = options.filter((opt) => {
        const label = typeof opt === 'string' ? opt : opt[labelKey];
        return label?.toString().toLowerCase().includes(search.toLowerCase());
    });

    const selectedOption = options.find((opt) => {
        const optValue = typeof opt === 'string' ? opt : opt[valueKey];
        return String(optValue) === String(value);
    });

    const selectedLabel = selectedOption
        ? (typeof selectedOption === 'string' ? selectedOption : selectedOption[labelKey])
        : '';

    const handleSelect = (opt) => {
        const optValue = typeof opt === 'string' ? opt : opt[valueKey];
        onChange(String(optValue));
        setIsOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('all');
        setSearch('');
    };

    const hasValue = value && value !== 'all';

    const dropdownPanel = isOpen ? createPortal(
        <div
            ref={dropdownRef}
            style={dropdownStyle}
            className={`${small ? 'tw-min-w-[160px]' : 'tw-min-w-[180px]'} tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-shadow-lg`}
        >
            {/* Search Input */}
            <div className={`${small ? 'tw-p-1' : 'tw-p-1.5'} tw-border-b tw-border-gray-100`}>
                <div className="tw-relative">
                    <Icon icon="mdi:magnify" className={`tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 ${small ? 'tw-text-xs' : 'tw-text-sm'}`} />
                    <input
                        ref={inputRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search..."
                        className={`tw-w-full ${small ? 'tw-pl-6 tw-pr-2 tw-py-1 tw-text-xs' : 'tw-pl-7 tw-pr-2 tw-py-1.5 tw-text-sm'} tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-border-blue-500`}
                    />
                </div>
            </div>

            {/* Options List */}
            <div className={`${small ? 'tw-max-h-[180px]' : 'tw-max-h-[200px]'} tw-overflow-y-auto`}>
                {loading ? (
                    <div className={`tw-px-3 tw-py-3 ${small ? 'tw-text-xs' : 'tw-text-sm'} tw-text-gray-400 tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-2`}>
                        <div className="tw-animate-spin tw-rounded-full tw-h-3.5 tw-w-3.5 tw-border-2 tw-border-gray-300 tw-border-t-blue-500"></div>
                        Loading...
                    </div>
                ) : filteredOptions.length > 0 ? (
                    filteredOptions.map((opt, index) => {
                        const optValue = typeof opt === 'string' ? opt : opt[valueKey];
                        const optLabel = typeof opt === 'string' ? opt : opt[labelKey];
                        const isSelected = String(optValue) === String(value);

                        return (
                            <button
                                key={optValue ?? index}
                                type="button"
                                onClick={() => handleSelect(opt)}
                                className={`tw-w-full tw-text-left ${small ? 'tw-px-2.5 tw-py-1.5 tw-text-xs' : 'tw-px-3 tw-py-2 tw-text-sm'} tw-transition-colors ${
                                    isSelected
                                        ? 'tw-bg-blue-50 tw-text-blue-700 tw-font-medium'
                                        : 'tw-text-gray-700 hover:tw-bg-gray-50'
                                }`}
                            >
                                {optLabel}
                            </button>
                        );
                    })
                ) : (
                    <div className={`tw-px-3 tw-py-3 ${small ? 'tw-text-xs' : 'tw-text-sm'} tw-text-gray-400 tw-text-center`}>
                        No results found
                    </div>
                )}
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <div ref={containerRef} className={`tw-relative ${className}`}>
            {/* Trigger Button */}
            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                style={{ border: isOpen ? '1px solid #3b82f6' : '1px solid #d1d5db' }}
                className={`tw-w-full tw-flex tw-items-center tw-gap-1.5 ${small ? 'tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-min-w-[100px]' : 'tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-min-w-[120px]'} tw-bg-white tw-text-left focus:tw-outline-none ${
                    isOpen ? 'tw-ring-1 tw-ring-blue-500' : ''
                } ${
                    disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'
                }`}
            >
                <span className={`tw-flex-1 tw-truncate ${hasValue ? 'tw-text-gray-800' : 'tw-text-gray-400'}`}>
                    {loading ? 'Loading...' : hasValue ? selectedLabel : placeholder}
                </span>
                {loading ? (
                    <div className="tw-animate-spin tw-rounded-full tw-h-3.5 tw-w-3.5 tw-border-2 tw-border-gray-300 tw-border-t-blue-500 tw-flex-shrink-0"></div>
                ) : clearable && hasValue ? (
                    <Icon
                        icon="mdi:close-circle"
                        className="tw-text-gray-400 hover:tw-text-red-500 tw-text-sm tw-flex-shrink-0"
                        onClick={handleClear}
                    />
                ) : (
                    <Icon
                        icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                        className="tw-text-gray-400 tw-text-sm tw-flex-shrink-0"
                    />
                )}
            </button>
            {dropdownPanel}
        </div>
    );
};

export default SearchableDropdown;
